'use client';

import { useState, useRef, useEffect } from 'react';
import { Star } from 'lucide-react';
import { ProductReview } from '@/types/products';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from '@/context/ToastContext';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/translations';


interface AddReviewProps {
  productId: string;
  onReviewSubmit: (review: ProductReview) => Promise<{ status: number } | undefined>;
}

export function AddReview({ productId, onReviewSubmit }: AddReviewProps) {
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [title, setTitle] = useState('');
    const [userName, setUserName] = useState('');
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const { toast } = useToast();
    const { language } = useLanguage();
    const t = useTranslation(language);

    const toastRef = useRef(toast);

    useEffect(() => {
        toastRef.current = toast;
    }, [toast]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (rating === 0) {
            toastRef.current?.({
                title: t('review.ratingRequired'),
                description: t('review.ratingRequiredDesc'),
            });
            return;
        }

        setIsSubmitting(true);
        setShowSuccess(false);
        
        try {
            const response = await onReviewSubmit({
                id: uuidv4(),
                productId,
                userId: 'anonymous',
                userName,
                rating,
                title,
                comment,
                verifiedPurchase: false,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });

            if (response && response.status === 200) {
                // Reset form after successful submission
                setRating(0);
                setTitle('');
                setComment('');
                setUserName('');
                setShowSuccess(true);
                
                setTimeout(() => {
                    toastRef.current?.({
                        title: t('review.submitted'),
                        description: t('review.submittedDesc'),
                    });
                }, 100);
                
                // Hide success message after 5 seconds
                setTimeout(() => {
                    setShowSuccess(false);
                }, 5000);
            } else {
                throw new Error('Failed to submit review');
            }
        } catch (error) {
            console.error('Failed to submit review:', error);
            
            setTimeout(() => {
                toastRef.current?.({
                    title: 'Review Submission Failed',
                    description: t('review.errorDesc'),
                });
            }, 100);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div style={{ paddingTop: 'var(--space-6)', borderTop: '1px solid var(--color-divider)' }}>
            <p style={{ fontFamily: 'var(--font-heading)', fontSize: 17, margin: '0 0 var(--space-4)' }}>{t('product.writeReview')}</p>

            {showSuccess && (
                <div className="card" style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3) var(--space-4)', background: 'var(--color-accent-2-100)' }}>
                    <p style={{ fontWeight: 600, color: 'var(--color-accent-2-700)', margin: 0 }}>{t('review.submitted')}</p>
                    <p style={{ fontSize: 13.5, color: 'var(--color-accent-2-700)', margin: '2px 0 0' }}>{t('review.submittedDesc')}</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
                <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                        {t('review.title')} <span style={{ color: 'var(--color-accent)' }}>*</span>
                    </label>
                    <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <button
                                key={star}
                                type="button"
                                style={{ padding: 4, background: 'none', border: 'none' }}
                                onClick={() => setRating(star)}
                                onMouseEnter={() => setHoverRating(star)}
                                onMouseLeave={() => setHoverRating(0)}
                                aria-label={`Rate ${star} out of 5`}
                            >
                                <Star
                                    className={`h-5 w-5 ${(hoverRating || rating) >= star ? 'text-yellow-400 fill-yellow-400' : ''}`}
                                    style={(hoverRating || rating) >= star ? undefined : { color: 'var(--color-neutral-400)' }}
                                />
                            </button>
                        ))}
                    </div>
                </div>

                <div className="field">
                    <label htmlFor="userName">{t('review.name')}</label>
                    <input
                        type="text"
                        id="userName"
                        className="input"
                        value={userName}
                        onChange={(e) => setUserName(e.target.value)}
                        placeholder="Enter your name"
                        required
                    />
                </div>

                <div className="field">
                    <label htmlFor="title">Review Title</label>
                    <input
                        type="text"
                        id="title"
                        className="input"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Great product!"
                        required
                        maxLength={100}
                    />
                </div>

                <div className="field">
                    <label htmlFor="comment">{t('review.comment')} <span style={{ color: 'var(--color-accent)' }}>*</span></label>
                    <textarea
                        id="comment"
                        rows={4}
                        className="input"
                        style={{ fontFamily: 'var(--font-body)' }}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Share your experience with this product..."
                        required
                    />
                </div>

                <div className="flex justify-end">
                    <button type="submit" disabled={isSubmitting || rating === 0} className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
                        {isSubmitting ? t('review.submitting') : t('review.submitButton')}
                    </button>
                </div>
            </form>
        </div>
    );
}
